/**
 * THE SEND BLOCKER — pure, zero imports, so verify-reviews.mjs drives the real
 * thing under Node rather than a copy of it.
 *
 * Making the draft body EDITABLE re-opens a hazard that the read-only preview
 * did not have. Today every draft arrives from the backend fully merged — no
 * `{{token}}`, no "Hi Coach!" fallback; that was measured on the live app on
 * 2026-09-11 across two accounts and two types before this build started. The
 * moment a rep can type into the body, template text can reach a real customer:
 * they paste a snippet from somewhere, or start a line they mean to finish.
 *
 * So an unresolved token BLOCKS the send. It does not warn, and it does not
 * colour the field red and let the mailto through — a warning on the way to a
 * customer is a warning nobody reads at 4pm. `Open in mail`, `Copy` and
 * `I sent it — record the ask` all refuse while one is present.
 *
 * The patterns are deliberately narrow. A false positive here is not free: it
 * blocks a rep from sending a perfectly good email and they lose trust in the
 * check, which is how a check ends up switched off. So `[1]` is not a token
 * (a citation), `<https://…>` is not a token (a bracketed URL), and `%20` is
 * not a token (an escape). Each pattern below names what it deliberately lets
 * through.
 */

/** Each entry: [name, regex, humanDescription] — regex must be global. */
const PATTERNS = [
  // {{name}} / {{ first_name }} — the mustache family, any spacing.
  ['mustache', /\{\{[^}]*\}\}/g, 'a {{…}} merge field'],

  // {name} — single brace. Lets through anything with a space or punctuation
  // that real prose would carry, so "{" alone in a sentence is not a match.
  //
  // ⚠️ THE LOOKAROUNDS ARE Löad-BEARING, and verify-reviews.mjs §2 caught their
  // absence. Without them "{{first_name}}" matches TWICE — once as a mustache
  // and once as the inner "{first_name}" — so a draft with two merge fields
  // reported four. Double-counting is not cosmetic here: the block reason
  // quotes the tokens it found, and telling a rep to fix four things when
  // there are two sends them hunting for two that do not exist.
  ['brace', /(?<!\{)\{[A-Za-z_][A-Za-z0-9_.]{0,40}\}(?!\})/g, 'a {…} merge field'],

  // [First name] / [SCHOOL] / [coach name] — bracketed instruction text.
  // Requires a letter first, so a numeric citation like [1] or [2] is NOT a
  // token. Allows internal spaces because "[coach name]" is the common shape.
  ['bracket', /\[[A-Za-z][A-Za-z0-9 ._#'-]{1,60}\]/g, 'a […] placeholder'],

  // <first_name> — angle placeholder. Lowercase/underscore only and no spaces,
  // no "@", no "/", so <https://sr.com> and <brian@sr.com> are NOT tokens.
  ['angle', /<[a-z][a-z0-9_.-]{1,30}>/g, 'a <…> placeholder'],

  // %FIRSTNAME% — the uppercase percent family. Requires a leading letter, so
  // a percent-escape like %20 in a pasted URL is NOT a token.
  ['percent', /%[A-Z][A-Z0-9_]{1,30}%/g, 'a %…% merge field'],

  // Left-in editing marks and values that mean "this never got filled in".
  // Word-boundaried: "a todo list" does not match TODO, and "Nullarbor" does
  // not match null.
  ['marker', /\b(TBD|TODO|FIXME|XXX+|undefined|null|NaN)\b/g, 'an unfilled value'],

  // Filler copy.
  ['lorem', /\blorem ipsum\b/gi, 'placeholder filler text'],
]

/**
 * Every unresolved token in `text`, in the order they appear, deduped by the
 * literal matched string.
 * @returns {{token:string, kind:string, why:string}[]}
 */
export function unresolvedTokens(text) {
  if (typeof text !== 'string' || !text) return []
  const seen = new Set()
  const out = []
  for (const [kind, re, why] of PATTERNS) {
    // Fresh lastIndex each call — a module-level /g regex is stateful.
    re.lastIndex = 0
    let m
    while ((m = re.exec(text)) !== null) {
      const token = m[0]
      if (seen.has(token)) continue
      seen.add(token)
      out.push({ token, kind, why })
    }
  }
  // Report in the order a reader would find them, not in pattern order.
  return out.sort((a, b) => text.indexOf(a.token) - text.indexOf(b.token))
}

/** True when this text is safe to put in front of a customer. */
export function isSendable(text) {
  return unresolvedTokens(text).length === 0
}

/**
 * The reason, phrased for the rep. Names the actual token — "unresolved
 * placeholder" on its own sends somebody hunting through nine paragraphs.
 */
export function blockReason(text) {
  const found = unresolvedTokens(text)
  if (!found.length) return null
  const list = found.slice(0, 3).map((f) => `“${f.token}”`).join(', ')
  const more = found.length > 3 ? ` and ${found.length - 3} more` : ''
  return `This draft still contains ${list}${more}. Nothing sends until that is` +
    ' replaced with the real words — a merge field reaching a customer is worse' +
    ' than a late ask.'
}

/* ---- the photo ask --------------------------------------------------- */

/**
 * BRIAN'S OWN LINE, kept in his construction rather than rewritten.
 * His copy: "If you have a picture of your ring or your team with the rings,
 * please add that too!" — second person, one ask, no justification after it.
 *
 * The only edits are to match the drafts' register, which does not use
 * exclamation marks anywhere: the "!" is dropped and "please" moves out of the
 * way of the verb. Nothing is added. An earlier pass wanted to append a reason
 * ("it is the part people stop to look at") — that is the model explaining the
 * ask to a coach who does not need it explained, so it is not here.
 *
 * This is a CLIENT-SIDE DEFAULT. The rep can cut it before sending, and the
 * backend composer is where it eventually belongs — see
 * docs/BACKEND-HANDOVER.md §10.
 */
export const PHOTO_LINE =
  'If you have a picture of your ring, or of the team with them, add that too.'

/** Where the photo ask is appropriate. */
export const PHOTO_TYPES = new Set(['internal', 'google', 'facebook', 'instagram', 'tiktok', 'x'])

/** Already there? Cheap containment check on the distinctive clause. */
export function hasPhotoLine(body) {
  return typeof body === 'string' && body.includes('picture of your ring')
}

/**
 * Insert the photo ask after the paragraph that makes the request, which in
 * every shipped draft is the one containing "in writing" or "would you".
 * Falling back to "before the sign-off" rather than "at the end" matters: at
 * the end it lands under "Thanks, Brian Kass" and reads like a postscript
 * somebody forgot to move.
 */
export function withPhotoLine(body,step='ask') {
  if(step!=='ask')return body
  if (typeof body !== 'string' || !body) return body
  if (hasPhotoLine(body) || /if you share a photo of the rings/i.test(body)) return body

  const paras = body.split(/\n\n+/)
  // Place the optional photo detail with the review request, before the actual
  // signature. New prose must not depend on a particular old ask phrase.
  const signatureAt = paras.findIndex(p => /^Thanks,\n/.test(p))
  const at = signatureAt >= 0 ? signatureAt : paras.length
  paras.splice(at, 0, PHOTO_LINE)
  return paras.join('\n\n')
}

/** Remove it again, so the toggle is genuinely two-way. */
export function withoutPhotoLine(body) {
  if (typeof body !== 'string' || !body) return body
  return body
    .split(/\n\n+/)
    .filter((p) => p.trim() !== PHOTO_LINE)
    .join('\n\n')
}
